<template>
  <div class="p-0">
    <div class="mb-5 flex items-center justify-between gap-3">
      <button type="button" @click="goBack" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900">
        <i class="fa-solid fa-arrow-left mr-2"></i>
        {{ isManagerMode ? 'Volver a bandeja' : 'Volver a mis solicitudes' }}
      </button>
      <span v-if="isEditMode && formStatus" class="px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(formStatus)">
        {{ statusLabel(formStatus) }}
      </span>
    </div>

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
      <p class="mt-1 text-sm text-gray-600">{{ pageSubtitle }}</p>
    </div>

    <section v-if="isManagerMode" class="wizard-card mb-5">
      <div class="flex flex-col lg:flex-row lg:items-end gap-4">
        <div class="flex-1">
          <label class="label">Docente beneficiario *</label>
          <select v-model="beneficiaryUserId" class="field" :disabled="isEditMode && Boolean(beneficiaryUserId)" @change="onBeneficiaryChange">
            <option value="">Selecciona el docente beneficiario</option>
            <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
              {{ teacherFullName(teacher) }}{{ teacherMeta(teacher) ? ` · ${teacherMeta(teacher)}` : '' }}
            </option>
          </select>
        </div>
        <div v-if="selectedBeneficiary" class="flex-1 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-indigo-600">Seleccionado</div>
          <div class="font-semibold text-indigo-950">{{ teacherFullName(selectedBeneficiary) }}</div>
          <div class="text-xs text-indigo-700">{{ teacherMeta(selectedBeneficiary) || 'Docente activo' }}</div>
        </div>
      </div>
      <p v-if="!beneficiaryUserId" class="mt-3 text-sm text-red-700">
        Selecciona el docente antes de continuar. El cupo y la solicitud se registraran a su nombre.
      </p>
    </section>

    <div class="bg-white border border-gray-200 rounded-xl shadow-sm mb-5 p-4">
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button
          v-for="(step, index) in visibleSteps"
          :key="step.key"
          type="button"
          @click="goToStep(index)"
          class="text-left"
        >
          <div class="flex items-center gap-2">
            <span
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border"
              :class="stepClass(index)"
            >
              <i v-if="index < currentStep" class="fa-solid fa-check text-xs"></i>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span class="hidden sm:block h-px flex-1 bg-gray-200"></span>
          </div>
          <div class="mt-2 text-xs font-semibold" :class="index === currentStep ? 'text-indigo-900' : 'text-gray-500'">
            {{ step.label }}
          </div>
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else>
      <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {{ error }}
      </div>
      <div v-if="success" class="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
        {{ success }}
      </div>

      <section v-if="activeStep.key === 'article'" class="wizard-card">
        <h2 class="section-title">Elegí el artículo</h2>
        <p class="section-subtitle">{{ articleSubtitle }}</p>

        <div class="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="md:col-span-2">
            <label class="label">Buscar</label>
            <input v-model="articleSearch" type="text" class="field" placeholder="Buscar por nombre o código..." />
          </div>
          <div>
            <label class="label">Categoría</label>
            <select v-model="articleCategory" @change="fetchArticles" class="field">
              <option v-for="category in categories" :key="category.value" :value="category.value">
                {{ category.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-5 space-y-3">
          <button
            v-for="article in filteredArticles"
            :key="article.id"
            type="button"
            @click="selectArticle(article)"
            class="w-full text-left border rounded-lg p-4 transition-colors"
            :class="form.leave_article_id === article.id ? 'border-indigo-700 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-200'"
          >
            <div class="flex justify-between gap-3">
              <div>
                <h3 class="font-semibold text-gray-900">{{ articleTitle(article) }}</h3>
                <p class="text-sm text-gray-500">
                  {{ categoryLabel(article.category) }}
                  <span v-if="article.requires_documentation"> · Requiere documentación</span>
                </p>
              </div>
              <span class="w-5 h-5 rounded-full border flex items-center justify-center" :class="form.leave_article_id === article.id ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-gray-300'">
                <i v-if="form.leave_article_id === article.id" class="fa-solid fa-check text-xs"></i>
              </span>
            </div>
          </button>

          <div v-if="filteredArticles.length === 0" class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-4">
            No hay artículos para los filtros seleccionados.
          </div>
        </div>

        <div class="mt-5 bg-blue-50 border border-blue-100 text-blue-800 rounded-md px-4 py-3 text-sm">
          <i class="fa-solid fa-circle-info mr-2"></i>
          El cupo se calcula después de elegir las fechas.
        </div>
      </section>

      <section v-else-if="activeStep.key === 'dates'" class="wizard-card">
        <h2 class="section-title">Definí las fechas</h2>
        <p class="section-subtitle">Indicá el período en el que necesitás la licencia o franquicia.</p>

        <SelectedArticleCard :article="selectedArticle" @change="currentStep = 0" />

        <div class="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="label">Desde *</label>
            <input v-model="form.start_date" type="date" class="field" />
          </div>
          <div>
            <label class="label">Hasta *</label>
            <input
              v-model="form.end_date"
              type="date"
              class="field"
              :disabled="!allowsMultiDay"
              :class="{ 'bg-gray-50 cursor-not-allowed': !allowsMultiDay }"
            />
            <p v-if="!allowsMultiDay" class="mt-1 text-xs text-gray-500">
              Este artículo solo permite un día por solicitud.
            </p>
          </div>
        </div>

        <div v-if="dateError" class="mt-3 bg-red-50 border border-red-100 text-red-700 rounded-md px-4 py-3 text-sm">
          {{ dateError }}
        </div>

        <div class="mt-5">
          <label class="label">Modalidad *</label>
          <div class="flex flex-wrap gap-4">
            <label class="inline-flex items-center gap-2 text-sm text-gray-700">
              <input v-model="form.full_day" :value="true" type="radio" />
              Día completo
            </label>
            <label
              class="inline-flex items-center gap-2 text-sm"
              :class="allowsPartialHours ? 'text-gray-700' : 'text-gray-400 cursor-not-allowed'"
            >
              <input v-model="form.full_day" :value="false" type="radio" :disabled="!allowsPartialHours" />
              Horario parcial
            </label>
          </div>
          <p v-if="!allowsPartialHours" class="mt-1 text-xs text-gray-500">
            Este artículo solo permite día completo.
          </p>
        </div>

        <div v-if="!form.full_day" class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="label">Desde hora</label>
            <input v-model="form.start_time" type="time" class="field" />
          </div>
          <div>
            <label class="label">Hasta hora</label>
            <input v-model="form.end_time" type="time" class="field" />
          </div>
          <div>
            <label class="label">Horas solicitadas</label>
            <input :value="requestedHours" readonly class="field bg-gray-50" />
          </div>
        </div>

        <div class="mt-5 bg-indigo-50 border border-indigo-100 rounded-md px-4 py-3 text-sm text-indigo-900">
          <i class="fa-regular fa-calendar mr-2"></i>
          {{ summaryText }}
        </div>
      </section>

      <section v-else-if="activeStep.key === 'quota'" class="wizard-card">
        <h2 class="section-title">{{ quotaStatusTitle }}</h2>
        <p class="section-subtitle">{{ quotaSubtitle }}</p>

        <div class="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div class="lg:col-span-2 border border-gray-200 rounded-lg p-4">
            <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <div v-if="isManagerMode" class="mb-3">
                  <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Docente</div>
                  <div class="mt-1 font-semibold text-gray-900">{{ beneficiaryLabel }}</div>
                </div>
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Artículo seleccionado</div>
                <div class="mt-1 font-semibold text-gray-900">{{ articleTitle(selectedArticle) }}</div>
              </div>
              <div>
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Fechas y duración</div>
                <div class="mt-1 font-semibold text-gray-900">{{ summaryText }}</div>
              </div>
            </div>

            <div class="mt-6">
              <div class="flex justify-between text-sm font-semibold text-gray-700">
                <span>Saldo de días</span>
                <span>{{ quotaProgressText }}</span>
              </div>
              <div v-if="quotaHasLimit" class="mt-2 h-3 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-indigo-800 rounded-full" :style="{ width: `${quotaProgressPercent}%` }"></div>
              </div>
              <div v-else class="mt-2 border border-dashed border-gray-300 rounded-md p-4 text-center text-sm text-gray-600">
                Sin límite numérico configurado
              </div>
            </div>

            <div class="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
              <div class="bg-gray-50 rounded-md p-3">
                <div class="stat-label">Usados</div>
                <div class="stat-value">{{ availability?.used_days ?? 0 }}</div>
              </div>
              <div class="bg-gray-50 rounded-md p-3">
                <div class="stat-label">Disponibles</div>
                <div class="stat-value">{{ quotaAvailableText }}</div>
              </div>
              <div class="bg-indigo-800 text-white rounded-md p-3">
                <div class="text-xs uppercase tracking-wide opacity-75">Esta solicitud</div>
                <div class="text-lg font-bold">{{ requestedDays }} día{{ requestedDays === 1 ? '' : 's' }}</div>
              </div>
              <div class="bg-gray-50 rounded-md p-3">
                <div class="stat-label">Quedarían</div>
                <div class="stat-value">{{ remainingDaysText }}</div>
              </div>
            </div>

            <div
              class="mt-5 rounded-md px-4 py-3 text-sm"
              :class="canContinueByQuota ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-700'"
            >
              <i class="mr-2" :class="canContinueByQuota ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-exclamation'"></i>
              {{ availability?.message || quotaFallbackMessage }}
            </div>
          </div>

          <aside class="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 class="font-semibold text-gray-900 mb-3">
              <i class="fa-solid fa-circle-info mr-2"></i>
              Reglas del artículo
            </h3>
            <ul v-if="articleRules.length" class="space-y-2 text-sm text-gray-700">
              <li v-for="rule in articleRules" :key="rule.id || rule.code" class="flex gap-2">
                <span>•</span>
                <span>{{ ruleText(rule) }}</span>
              </li>
            </ul>
            <p v-else class="text-sm text-gray-500">No hay reglas adicionales para mostrar.</p>
          </aside>
        </div>
      </section>

      <section v-else-if="activeStep.key === 'reason'" class="wizard-card">
        <h2 class="section-title">Revisá y confirmá</h2>
        <p class="section-subtitle">Verificá los datos antes de enviar la solicitud.</p>

        <div v-if="requestObservation" class="mt-5 bg-orange-50 border border-orange-100 text-orange-800 rounded-md px-4 py-3 text-sm">
          <strong>Observación de dirección:</strong> {{ requestObservation }}
        </div>

        <div class="mt-5 border border-gray-200 rounded-lg p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
            <div>
              <div v-if="isManagerMode" class="mb-4">
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Docente</div>
                <div class="mt-1 font-semibold text-gray-900">{{ beneficiaryLabel }}</div>
              </div>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Artículo</div>
              <div class="mt-1 font-semibold text-gray-900">{{ articleTitle(selectedArticle) }}</div>
              <button type="button" @click="currentStep = 0" class="mt-1 text-xs font-semibold text-indigo-700">Editar artículo</button>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Fechas y duración</div>
              <div class="mt-1 font-semibold text-gray-900">{{ summaryText }}</div>
              <button type="button" @click="currentStep = 1" class="mt-1 text-xs font-semibold text-indigo-700">Editar fechas</button>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Cupo</div>
              <div class="mt-1 font-semibold" :class="canContinueByQuota ? 'text-green-700' : 'text-red-700'">
                {{ canContinueByQuota ? 'Suficiente' : 'Insuficiente' }}
              </div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Documentación</div>
              <div class="mt-1 font-semibold text-gray-900">{{ requiresDocumentation ? 'Requerida' : 'No requerida' }}</div>
            </div>
          </div>
        </div>

        <div class="mt-5">
          <label class="label">Motivo / observaciones *</label>
          <textarea
            v-model="form.reason"
            rows="5"
            maxlength="500"
            class="field"
            :placeholder="isManagerMode ? 'Describe brevemente el motivo informado por el docente...' : 'Describí brevemente el motivo de tu solicitud...'"
          ></textarea>
          <div class="mt-1 text-right text-xs text-gray-500">{{ form.reason.length }} / 500 caracteres</div>
        </div>

        <div class="mt-5 border border-gray-200 rounded-lg p-4">
          <h3 class="font-semibold text-gray-900">Requisitos a cumplir</h3>
          <ul v-if="articleRequirements.length" class="mt-3 space-y-2 text-sm text-gray-700">
            <li v-for="requirement in articleRequirements" :key="requirement.id || requirement.code" class="flex items-start gap-2">
              <i class="fa-regular fa-circle-check mt-0.5 text-gray-400"></i>
              <span>{{ requirement.name || requirement.code }} <span v-if="requirement.is_required" class="text-xs text-gray-500">(Obligatorio)</span></span>
            </li>
          </ul>
          <p v-else class="mt-2 text-sm text-gray-500">Dirección verificará los requisitos aplicables al artículo.</p>
        </div>
      </section>

      <section v-else-if="activeStep.key === 'docs'" class="wizard-card">
        <h2 class="section-title">Documentación requerida</h2>
        <p class="section-subtitle">{{ docsSubtitle }}</p>

        <div class="mt-5 bg-blue-50 border border-blue-100 text-blue-800 rounded-md px-4 py-3 text-sm">
          Este artículo requiere documentación. La solicitud puede quedar observada hasta que dirección apruebe los archivos.
        </div>

        <div class="mt-5 border border-gray-200 rounded-lg p-4">
          <h3 class="font-semibold text-gray-900">Documentos esperados</h3>
          <ul v-if="articleRequirements.length" class="mt-3 space-y-2">
            <li v-for="requirement in documentRequirements" :key="requirement.id || requirement.code" class="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
              <span>{{ requirement.name || requirement.code }}</span>
              <span v-if="requirement.is_required" class="text-xs font-semibold text-red-700 bg-red-50 rounded px-2 py-1">Obligatorio</span>
            </li>
          </ul>
          <p v-else class="mt-2 text-sm text-gray-500">Adjuntá la documentación respaldatoria del artículo.</p>
        </div>

        <div class="mt-5 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <div class="mx-auto w-12 h-12 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center mb-3">
            <i class="fa-solid fa-cloud-arrow-up text-xl"></i>
          </div>
          <label class="inline-flex items-center px-4 py-2 bg-indigo-700 text-white rounded-md hover:bg-indigo-800 cursor-pointer">
            Seleccionar archivo
            <input type="file" class="hidden" @change="onFileSelected" accept=".pdf,.jpg,.jpeg,.png" />
          </label>
          <p class="mt-2 text-xs text-gray-500">PDF, JPG, PNG — máximo 32 MB por archivo</p>
        </div>

        <div class="mt-4">
          <label class="label">Tipo de documento</label>
          <select v-model="selectedDocumentType" class="field">
            <option value="">General</option>
            <option v-for="requirement in documentRequirements" :key="requirement.code" :value="requirement.code">
              {{ requirement.name || requirement.code }}
            </option>
          </select>
        </div>

        <div class="mt-5 border border-gray-200 rounded-lg overflow-hidden">
          <div class="bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Archivos cargados ({{ documents.length }})
          </div>
          <div v-if="documents.length === 0" class="p-4 text-sm text-gray-500">Todavía no subiste archivos.</div>
          <div v-else class="divide-y divide-gray-100">
            <div v-for="document in documents" :key="document.id || document.document_id || document.file_name" class="p-4 flex items-center justify-between gap-3">
              <div class="min-w-0">
                <div class="font-semibold text-sm text-gray-900 truncate">{{ document.file_name || document.name || document.original_name || 'Documento' }}</div>
                <div class="text-xs text-gray-500 truncate">
                  {{ document.document_type || document.type || 'General' }}
                  <span v-if="document.status"> · {{ document.status }}</span>
                </div>
              </div>
              <i class="fa-regular fa-file-pdf text-red-500"></i>
            </div>
          </div>
        </div>

        <div v-if="requiresDocumentation && documents.length === 0" class="mt-4 bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-md px-4 py-3 text-sm">
          Para enviar, adjuntá al menos un archivo.
        </div>
      </section>

      <div class="sticky bottom-0 mt-6 -mx-0 bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          @click="previousStep"
          :disabled="currentStep === 0 || saving"
          class="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <i class="fa-solid fa-arrow-left mr-2"></i>
          Atrás
        </button>
        <div class="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            v-if="currentStep > 0"
            type="button"
            @click="saveDraft"
            :disabled="saving || !canSaveDraft"
            class="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {{ saving ? 'Guardando...' : 'Guardar borrador' }}
          </button>
          <button
            v-if="!isLastStep"
            type="button"
            @click="nextStep"
            :disabled="saving || !canGoNext"
            class="px-4 py-2 text-sm font-semibold text-white bg-indigo-700 rounded-md hover:bg-indigo-800 disabled:opacity-50"
          >
            Siguiente <i class="fa-solid fa-arrow-right ml-2"></i>
          </button>
          <button
            v-else
            type="button"
            @click="submitRequest"
            :disabled="saving || !canSubmit"
            class="px-4 py-2 text-sm font-semibold text-white bg-indigo-700 rounded-md hover:bg-indigo-800 disabled:opacity-50"
          >
            {{ saving ? 'Enviando...' : 'Enviar solicitud' }}
            <i class="fa-regular fa-paper-plane ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveArticlesApi, leaveAvailabilityApi, leaveRequestsApi } from '@/service/leave.service';
import { teachersApi } from '@/service/user.service';
import {
  LEAVE_CATEGORIES,
  articleTitle,
  categoryLabel,
  daysBetweenInclusive,
  formatDate,
  requestDocuments,
  statusClass,
  statusLabel,
  teacherFullName,
  teacherProfile,
  todayISO,
  unwrapData,
  unwrapList,
} from '@/utils/leave';

const SelectedArticleCard = {
  props: {
    article: { type: Object, default: null },
  },
  emits: ['change'],
  methods: { articleTitle, categoryLabel },
  template: `
    <div class="mt-5 border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-3">
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Artículo seleccionado</div>
        <div class="mt-1 font-semibold text-gray-900">{{ articleTitle(article || {}) }}</div>
        <div class="text-sm text-gray-500">{{ categoryLabel(article?.category) }}</div>
      </div>
      <button type="button" @click="$emit('change')" class="text-sm font-semibold text-indigo-700">Cambiar</button>
    </div>
  `,
};

export default {
  name: 'LeaveRequestWizardView',
  components: { Loading, SelectedArticleCard },
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      success: '',
      currentStep: 0,
      requestId: this.$route.params.id || '',
      formStatus: '',
      requestObservation: '',
      articles: [],
      selectedArticle: null,
      articleRules: [],
      articleRequirements: [],
      availability: null,
      documents: [],
      teachers: [],
      beneficiaryUserId: this.$route.query.user_id || '',
      articleSearch: '',
      articleCategory: '',
      selectedDocumentType: '',
      categories: LEAVE_CATEGORIES,
      form: {
        leave_article_id: this.$route.query.article_id || '',
        start_date: todayISO(),
        end_date: todayISO(),
        full_day: true,
        start_time: '',
        end_time: '',
        requested_hours: null,
        reason: '',
      },
    };
  },
  computed: {
    currentUserId() {
      return this.$store.getters['authentication/currentUser']?.user_id;
    },
    isManagerMode() {
      return this.$route.query.as === 'manager' || Boolean(this.$route.query.user_id);
    },
    effectiveUserId() {
      return this.isManagerMode ? this.beneficiaryUserId : this.currentUserId;
    },
    selectedBeneficiary() {
      return this.teachers.find((teacher) => teacher.id === this.beneficiaryUserId) || null;
    },
    beneficiaryLabel() {
      return this.selectedBeneficiary ? teacherFullName(this.selectedBeneficiary) : 'Docente no seleccionado';
    },
    pageTitle() {
      if (this.isEditMode) return 'Editar solicitud';
      return this.isManagerMode ? 'Nueva solicitud para docente' : 'Nueva solicitud';
    },
    pageSubtitle() {
      return this.isManagerMode
        ? 'Carga una licencia o permiso a nombre de un docente beneficiario.'
        : 'Completá los datos de tu licencia o permiso docente.';
    },
    articleSubtitle() {
      return this.isManagerMode
        ? `La solicitud se registrara a nombre de ${this.beneficiaryLabel}.`
        : 'Seleccioná el tipo de licencia, justificación o franquicia.';
    },
    quotaSubtitle() {
      return this.isManagerMode
        ? `Verificamos si ${this.beneficiaryLabel} puede solicitar este articulo en las fechas elegidas.`
        : 'Verificamos si podés solicitar este artículo en las fechas elegidas.';
    },
    docsSubtitle() {
      return this.isManagerMode
        ? `Adjunta los archivos para la solicitud de ${this.beneficiaryLabel}.`
        : 'Adjuntá los archivos necesarios para completar tu solicitud.';
    },
    isEditMode() {
      return Boolean(this.requestId);
    },
    requiresDocumentation() {
      return Boolean(this.selectedArticle?.requires_documentation);
    },
    allowsMultiDay() {
      return Boolean(this.selectedArticle?.allows_multi_day);
    },
    allowsPartialHours() {
      return Boolean(this.selectedArticle?.allows_partial_hours);
    },
    steps() {
      return [
        { key: 'article', label: 'Artículo' },
        { key: 'dates', label: 'Fechas' },
        { key: 'quota', label: 'Cupo' },
        { key: 'reason', label: 'Motivo' },
        { key: 'docs', label: 'Docs' },
      ];
    },
    visibleSteps() {
      return this.requiresDocumentation ? this.steps : this.steps.filter((step) => step.key !== 'docs');
    },
    activeStep() {
      return this.visibleSteps[this.currentStep] || this.visibleSteps[0];
    },
    isLastStep() {
      return this.currentStep === this.visibleSteps.length - 1;
    },
    filteredArticles() {
      const term = this.articleSearch.trim().toLowerCase();
      if (!term) return this.articles;
      return this.articles.filter((article) =>
        [article.code, article.name, article.article_number, article.description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term)
      );
    },
    requestedDays() {
      return daysBetweenInclusive(this.form.start_date, this.form.end_date);
    },
    dateError() {
      if (!this.form.start_date || !this.form.end_date) return 'Completá fecha desde y hasta.';
      if (new Date(`${this.form.end_date}T00:00:00`) < new Date(`${this.form.start_date}T00:00:00`)) {
        return 'La fecha hasta no puede ser anterior al desde.';
      }
      if (!this.allowsMultiDay && this.form.start_date !== this.form.end_date) {
        return 'Este artículo solo permite un día por solicitud.';
      }
      return '';
    },
    requestedHours() {
      if (!this.form.start_time || !this.form.end_time) return '';
      const [startHour, startMinute] = this.form.start_time.split(':').map(Number);
      const [endHour, endMinute] = this.form.end_time.split(':').map(Number);
      const minutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
      if (minutes <= 0) return '';
      return Number((minutes / 60).toFixed(2));
    },
    summaryText() {
      const range = this.form.start_date === this.form.end_date
        ? formatDate(this.form.start_date)
        : `${formatDate(this.form.start_date)} → ${formatDate(this.form.end_date)}`;
      const duration = this.form.full_day
        ? `${this.requestedDays} día${this.requestedDays === 1 ? '' : 's'}`
        : `${this.requestedHours || 0} horas`;
      return `${range} · ${duration} · ${this.form.full_day ? 'Día completo' : 'Horario parcial'}`;
    },
    canSaveDraft() {
      return Boolean(this.effectiveUserId && this.form.leave_article_id && this.form.start_date && this.form.end_date);
    },
    canContinueByQuota() {
      return this.availability ? Boolean(this.availability.can_request) : true;
    },
    canGoNext() {
      if (this.isManagerMode && !this.beneficiaryUserId) return false;
      if (this.activeStep.key === 'article') return Boolean(this.form.leave_article_id);
      if (this.activeStep.key === 'dates') return !this.dateError && (!this.form.full_day ? Boolean(this.requestedHours) : true);
      if (this.activeStep.key === 'quota') return this.canContinueByQuota;
      if (this.activeStep.key === 'reason') return Boolean(this.form.reason.trim());
      return true;
    },
    canSubmit() {
      if (!this.form.reason.trim()) return false;
      if (this.isManagerMode && !this.beneficiaryUserId) return false;
      if (!this.canContinueByQuota) return false;
      if (this.requiresDocumentation && this.documents.length === 0) return false;
      return true;
    },
    quotaHasLimit() {
      return this.availability?.available_days !== null && this.availability?.available_days !== undefined;
    },
    quotaTotalDays() {
      return Number(this.availability?.used_days || 0) + Number(this.availability?.available_days || 0);
    },
    quotaProgressPercent() {
      if (!this.quotaTotalDays) return 0;
      return Math.min(100, Math.round((Number(this.availability?.used_days || 0) / this.quotaTotalDays) * 100));
    },
    quotaProgressText() {
      if (!this.quotaHasLimit) return `${this.availability?.used_days || 0} usos registrados`;
      return `${this.availability?.used_days || 0} de ${this.quotaTotalDays} días usados`;
    },
    quotaAvailableText() {
      return this.quotaHasLimit ? this.availability?.available_days : 'Sin límite';
    },
    remainingDaysText() {
      if (!this.quotaHasLimit) return 'Sin límite';
      const remaining = Number(this.availability?.available_days || 0) - this.requestedDays;
      return `${Math.max(0, remaining)} día${remaining === 1 ? '' : 's'}`;
    },
    quotaStatusTitle() {
      return this.canContinueByQuota ? 'Cupo disponible' : 'Cupo insuficiente';
    },
    quotaFallbackMessage() {
      if (this.canContinueByQuota) {
        return this.isManagerMode
          ? `${this.beneficiaryLabel} puede continuar con esta solicitud.`
          : 'Podés continuar con esta solicitud.';
      }
      return this.isManagerMode
        ? `${this.beneficiaryLabel} no tiene cupo suficiente para estas fechas.`
        : 'No tenés cupo suficiente para estas fechas.';
    },
    documentRequirements() {
      const docs = this.articleRequirements.filter((requirement) => requirement.requirement_type === 'document');
      return docs.length ? docs : this.articleRequirements;
    },
  },
  watch: {
    'form.full_day'(value) {
      if (value) {
        this.form.start_time = '';
        this.form.end_time = '';
        this.form.requested_hours = null;
      }
    },
    'form.start_date'(value) {
      if (!this.allowsMultiDay && value) {
        this.form.end_date = value;
      }
    },
    allowsMultiDay(value) {
      if (!value && this.form.start_date) {
        this.form.end_date = this.form.start_date;
      }
    },
    allowsPartialHours(value) {
      if (!value) {
        this.form.full_day = true;
      }
    },
  },
  async created() {
    await Promise.all([this.fetchArticles(), this.fetchTeachers()]);
    if (this.isEditMode) {
      await this.fetchRequest();
    } else if (this.form.leave_article_id) {
      const article = this.articles.find((item) => item.id === this.form.leave_article_id);
      if (article) await this.selectArticle(article);
    }
  },
  methods: {
    articleTitle,
    categoryLabel,
    statusClass,
    statusLabel,
    teacherFullName,
    stepClass(index) {
      if (index < this.currentStep) return 'bg-indigo-700 text-white border-indigo-700';
      if (index === this.currentStep) return 'bg-indigo-900 text-white border-indigo-900';
      return 'bg-gray-100 text-gray-500 border-gray-200';
    },
    async fetchArticles() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveArticlesApi.list({
          active: true,
          category: this.articleCategory,
        });
        this.articles = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los artículos.';
      } finally {
        this.loading = false;
      }
    },
    async fetchTeachers() {
      if (!this.isManagerMode) return;
      try {
        const response = await teachersApi.list({ status: 'activo' });
        this.teachers = unwrapList(response);
      } catch {
        this.teachers = [];
      }
    },
    async fetchRequest() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveRequestsApi.get(this.requestId);
        const request = unwrapData(response);
        this.formStatus = request.status;
        this.requestObservation = request.observation || request.observation_reason || request.last_observation || '';
        this.beneficiaryUserId = request.user_id || this.beneficiaryUserId;
        this.form = {
          leave_article_id: request.leave_article_id,
          start_date: request.start_date,
          end_date: request.end_date,
          full_day: request.full_day !== false,
          start_time: request.start_time || '',
          end_time: request.end_time || '',
          requested_hours: request.requested_hours || null,
          reason: request.reason || '',
        };
        this.documents = requestDocuments(request);
        const article = this.articles.find((item) => item.id === request.leave_article_id) || {
          id: request.leave_article_id,
          code: request.article_code,
          name: request.article_name,
          requires_documentation: Boolean(request.requires_documentation),
        };
        await this.selectArticle(article, false);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar la solicitud.';
      } finally {
        this.loading = false;
      }
    },
    async selectArticle(article, clearAvailability = true) {
      this.selectedArticle = article;
      this.form.leave_article_id = article.id;
      if (clearAvailability) this.availability = null;
      await Promise.all([this.fetchArticleDetails(article.id), this.fetchArticleRequirements(article.id)]);
    },
    async fetchArticleDetails(articleId) {
      try {
        const [detail, rules] = await Promise.all([
          leaveArticlesApi.get(articleId),
          leaveArticlesApi.listRules(articleId),
        ]);
        const detailData = unwrapData(detail);
        this.selectedArticle = { ...this.selectedArticle, ...detailData };
        this.articleRules = unwrapList(rules);
      } catch {
        this.articleRules = [];
      }
    },
    async fetchArticleRequirements(articleId) {
      try {
        const response = await leaveArticlesApi.listRequirements(articleId);
        this.articleRequirements = unwrapList(response);
      } catch {
        this.articleRequirements = [];
      }
    },
    async fetchAvailability() {
      if (!this.effectiveUserId || !this.form.leave_article_id || this.dateError) return;
      this.saving = true;
      this.error = '';
      try {
        const response = await leaveAvailabilityApi.get(this.effectiveUserId, {
          article_id: this.form.leave_article_id,
          date: this.form.start_date,
        });
        this.availability = unwrapData(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo calcular el cupo.';
      } finally {
        this.saving = false;
      }
    },
    payload(status = 'draft') {
      return {
        user_id: this.effectiveUserId,
        leave_article_id: this.form.leave_article_id,
        start_date: this.form.start_date,
        end_date: this.form.end_date,
        full_day: this.form.full_day,
        start_time: this.form.full_day ? undefined : this.form.start_time,
        end_time: this.form.full_day ? undefined : this.form.end_time,
        requested_hours: this.form.full_day ? undefined : this.requestedHours,
        reason: this.form.reason,
        status,
      };
    },
    async saveDraft() {
      if (!this.canSaveDraft) return null;
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        const response = this.requestId
          ? await leaveRequestsApi.update(this.requestId, this.payload('draft'))
          : await leaveRequestsApi.create(this.payload('draft'));
        const request = unwrapData(response);
        this.requestId = request.id || request.request_id || this.requestId;
        this.formStatus = request.status || 'draft';
        this.success = 'Borrador guardado.';
        return request;
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo guardar el borrador.';
        return null;
      } finally {
        this.saving = false;
      }
    },
    async nextStep() {
      if (!this.canGoNext) return;
      if (this.activeStep.key === 'dates') {
        this.currentStep += 1;
        await this.fetchAvailability();
        return;
      }
      if (this.activeStep.key === 'reason' && this.requiresDocumentation && !this.requestId) {
        const draft = await this.saveDraft();
        if (!draft) return;
      }
      this.currentStep = Math.min(this.currentStep + 1, this.visibleSteps.length - 1);
    },
    previousStep() {
      this.currentStep = Math.max(0, this.currentStep - 1);
    },
    goToStep(index) {
      if (index <= this.currentStep) this.currentStep = index;
    },
    async onFileSelected(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!this.requestId) {
        const draft = await this.saveDraft();
        if (!draft) return;
      }
      this.saving = true;
      this.error = '';
      this.success = '';
      try {
        const response = await leaveRequestsApi.addDocument(this.requestId, file, this.selectedDocumentType);
        const document = unwrapData(response);
        this.documents = [...this.documents, document];
        this.success = 'Documento cargado.';
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo subir el documento.';
      } finally {
        this.saving = false;
      }
    },
    async submitRequest() {
      this.error = '';
      this.success = '';
      const draft = await this.saveDraft();
      if (!draft || !this.requestId) return;
      this.saving = true;
      try {
        const response = await leaveRequestsApi.submit(this.requestId);
        const request = unwrapData(response);
        const id = request.id || this.requestId;
        this.$router.push(this.isManagerMode ? `/leave/requests/${id}/review` : `/leave/requests/${id}`);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo enviar la solicitud.';
      } finally {
        this.saving = false;
      }
    },
    ruleText(rule) {
      const parts = [];
      if (rule.period_type) parts.push(`Período: ${rule.period_type}`);
      if (rule.max_days) parts.push(`Máximo ${rule.max_days} días`);
      if (rule.annual_limit_days) parts.push(`Límite anual ${rule.annual_limit_days} días`);
      if (rule.count_pending_as_used) parts.push('Las pendientes cuentan como usadas');
      return parts.join(' · ') || rule.description || rule.name || 'Regla del artículo';
    },
    goBack() {
      this.$router.push(this.isManagerMode ? '/leave/inbox' : '/leave/requests');
    },
    onBeneficiaryChange() {
      this.availability = null;
      if (this.currentStep > 2) this.currentStep = 2;
    },
    teacherMeta(teacher) {
      const profile = teacherProfile(teacher);
      return [profile.file_number || profile.legajo, profile.dni ? `DNI ${profile.dni}` : ''].filter(Boolean).join(' · ');
    },
  },
};
</script>

<style scoped>
.wizard-card {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-5;
}
.section-title {
  @apply text-xl font-bold text-gray-900;
}
.section-subtitle {
  @apply mt-1 text-sm text-gray-600;
}
.label {
  @apply block text-xs font-semibold text-gray-500 mb-1;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.stat-label {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500;
}
.stat-value {
  @apply mt-1 text-lg font-bold text-gray-900;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * soft info-box and badge colors below that the retrofit doesn't cover
 * for anyone.
 */
.dark .wizard-card {
  @apply bg-gray-800 border-gray-700;
}
.dark .section-title {
  @apply text-gray-100;
}
.dark .section-subtitle {
  @apply text-gray-300;
}
.dark .label,
.dark .stat-label {
  @apply text-gray-400;
}
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .stat-value {
  @apply text-gray-100;
}

.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .text-indigo-600 {
  color: theme('colors.indigo.300');
}
.dark .text-indigo-950 {
  color: theme('colors.indigo.100');
}

.dark .border-red-100,
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-100,
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-100,
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-100,
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-100,
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .border-indigo-100 {
  border-color: rgba(99, 102, 241, 0.35);
}
.dark .border-indigo-700 {
  border-color: theme('colors.indigo.500');
}
.dark .border-indigo-900 {
  border-color: theme('colors.indigo.700');
}
.dark .hover\:border-indigo-200:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
</style>
